import { action, makeObservable, observable } from 'mobx';
import { API } from '~/api';
import { GridStateModel } from '~/model/model.gridstate';

import { Async } from '~/shared/tools/async';
import { Singleton } from '../shared/service/service.base';
import { ApiService } from './service.api';
import { UIService } from './service.ui';

@Singleton()
export class PostCommentService {
	readonly items = observable.array([] as API.PostCommentItem[], { deep: false });
	readonly async = new Async();
	readonly gridState = new GridStateModel('comments');

	@observable private postId = 0;
	@observable totalCount = 0;

	constructor(
		private api: ApiService,
		private ui: UIService,
	) {
		makeObservable(this);
	}

	@action.bound setPostId(postId: number) {
		this.postId = postId;
	}

	loadAndSubscribe = (postId: number | undefined, commentId: number | undefined) => {
		if (commentId) this.gridState.setFilterImmediate(commentId.toString());
		if (postId) this.setPostId(postId);
		this.reload();
		return this.gridState.subscribe(this.reload);
	};

	reload = () => {
		this.async.call(async () => {
			if (!this.postId) return;
			const { data } = await this.api.postCommentGetList(this.postId, {
				filter: this.gridState.filterQuery,
				limit: this.gridState.pageSize,
				offset: this.gridState.offset,
				sort: this.gridState.sort,
				sortDir: this.gridState.sortDir,
			});
			return () => {
				this.items.replace(data.items);
				this.totalCount = data.total;
			};
		});
	};

	delete = (postId: number | undefined, id: number | undefined) => {
		return this.async.call(async () => {
			if (!postId || !id) return;
			await this.api.postCommentDelete(postId, id);
			this.ui.notify('Comment deleted', 'warning');
		});
	};

	hide = (postId: number | undefined, id: number | undefined) => {
		return this.async.call(async () => {
			if (!postId || !id) return;
			await this.api.postCommentModerate(postId, id, { moderationStatus: 'HIDDEN' });
			this.ui.notify('Comment hidden', 'success');
		});
	};

	approve = (postId: number | undefined, id: number | undefined) => {
		return this.async.call(async () => {
			if (!postId || !id) return;
			await this.api.postCommentModerate(postId, id, { moderationStatus: 'APPROVED' });
			this.ui.notify('Comment approved', 'success');
		});
	};
}
